import { useCallback } from "react"
import { Layers, Search, X, Plus, Ban as BanIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { FilterControl, InlineOpacity } from "./layer-item"
import { PresetsMenu } from "./presets-menu"
import { LayerGroupSection } from "./layer-group-section"
import type { LayerPreset, LayerGroup, LayerDefinition } from "@/types/explore"

interface LayersPanelProps {
  layersOpen: boolean
  setLayersOpen: (open: boolean) => void
  activeLayers: string[]
  toggleLayer: (id: string) => void
  quickLayers: string[]
  quickLayerItems: LayerDefinition[]
  toggleQuickLayer: (id: string) => void
  activeCount: number
  filteredLayerGroups: LayerGroup[]
  layerSearch: string
  setLayerSearch: (search: string) => void
  layerPresets: LayerPreset[]
  savePreset: (name: string) => void
  loadPreset: (preset: LayerPreset) => void
  deletePreset: (index: number) => void
  setActiveLayers: (layers: string[]) => void
  setLoading?: (key: string, active: boolean) => void
  isToolActive?: boolean
  onFilterChange?: (layerId: string, filterId: string, value: unknown) => void
  dynamicOptions?: Record<string, string[]>
}

export const LayersPanel = ({
  layersOpen,
  setLayersOpen,
  activeLayers,
  toggleLayer,
  quickLayers,
  quickLayerItems,
  toggleQuickLayer,
  activeCount,
  filteredLayerGroups,
  layerSearch,
  setLayerSearch,
  layerPresets,
  savePreset,
  loadPreset,
  deletePreset,
  setActiveLayers,
  setLoading,
  isToolActive,
  onFilterChange,
  dynamicOptions,
}: LayersPanelProps) => {
  const searchInputRef = useCallback((node: HTMLInputElement | null) => {
    if (node) node.focus()
  }, [])

  const findLayerName = useCallback(
    (id: string): string => {
      for (const group of filteredLayerGroups) {
        const found = group.layers.find((l) => l.id === id)
        if (found) return found.name
        for (const sub of group.subgroups ?? []) {
          const subFound = sub.layers.find((l) => l.id === id)
          if (subFound) return subFound.name
        }
      }
      const quick = quickLayerItems.find((l) => l.id === id)
      return quick?.name ?? id
    },
    [filteredLayerGroups, quickLayerItems],
  )

  const handleToggleLayer = (id: string) => {
    if (!activeLayers.includes(id)) {
      setLoading?.(`layer-${findLayerName(id)}`, true)
    }
    toggleLayer(id)
  }

  const handleFilterChange = useCallback(
    (layerId: string, filterId: string, value: unknown) => {
      if (activeLayers.includes(layerId)) {
        setLoading?.(`layer-${findLayerName(layerId)}`, true)
      }
      onFilterChange?.(layerId, filterId, value)
    },
    [activeLayers, findLayerName, setLoading, onFilterChange],
  )

  return (
    <div
      data-tour-step="layers-panel"
      className={`absolute bottom-2 right-2 z-10 flex flex-col justify-end${isToolActive ? " pointer-events-none" : ""}`}
      style={{ top: "80px" }}
    >
      <div
        className={`layers-panel bg-white/95 backdrop-blur-sm map-panel-shadow rounded-xl w-72 pointer-events-auto overflow-hidden ${layersOpen ? "flex flex-col max-h-full" : ""}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-bold text-gray-800 tracking-tight flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            Calques
            {activeCount > 0 && (
              <span className="bg-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center leading-none tabular-nums">
                {activeCount}
              </span>
            )}
          </span>
          <div className="flex items-center gap-1">
            {activeLayers.length > 0 && (
              <button
                onClick={() => setActiveLayers([])}
                title="Tout desactiver"
                className="cursor-pointer p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors duration-150"
              >
                <BanIcon className="w-4 h-4" />
              </button>
            )}
            <PresetsMenu
              layerPresets={layerPresets}
              savePreset={savePreset}
              loadPreset={loadPreset}
              deletePreset={deletePreset}
            />
            <Button
              onClick={() => setLayersOpen(!layersOpen)}
              variant="ghost"
              size="icon-sm"
              className={`transition-colors duration-150 ${layersOpen ? "text-primary bg-primary/[0.08]" : "text-gray-400 hover:text-primary hover:bg-gray-100"}`}
            >
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${layersOpen ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </Button>
          </div>
        </div>

        <div className="w-full h-px bg-gray-100" />

        {!layersOpen ? (
          /* Quick mode */
          <div data-tour-step="quick-layers" className="px-2 py-2 space-y-1">
            {quickLayerItems.map((layer, i) => {
              const isActive = activeLayers.includes(layer.id)
              const opacityFilter = layer.filters?.find((f) => f.type === "slider")
              const quickFilters = layer.filters?.filter((f) => f.type !== "slider") || []
              return (
                <div key={layer.id}>
                  <button
                    onClick={() => handleToggleLayer(layer.id)}
                    className={`transition-all duration-150 cursor-pointer select-none active:scale-[0.97] w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left ${
                      isActive
                        ? "bg-primary/[0.08] border border-primary/20"
                        : "bg-gray-50 border border-transparent hover:bg-gray-100"
                    }`}
                  >
                    <div
                      className={`w-2 h-2 rounded-full shrink-0 transition-all duration-150 ${isActive ? "scale-125" : "scale-100"}`}
                      style={{
                        backgroundColor: isActive ? "#1E40AF" : "#CBD5E1",
                      }}
                    />
                    <span
                      className={`text-sm flex-1 transition-colors duration-100 ${isActive ? "text-primary font-medium" : "text-gray-500"}`}
                    >
                      {layer.name}
                    </span>
                    {isActive && opacityFilter && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <InlineOpacity onChange={(v) => handleFilterChange(layer.id, opacityFilter.id, v)} />
                      </div>
                    )}
                    <span className="relative inline-flex items-center justify-center w-5 h-5 shrink-0">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke={isActive ? "#FCC43E" : "#CBD5E1"}
                        strokeWidth={1.5}
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                        />
                      </svg>
                      <span
                        className="absolute font-bold text-[9px] top-[5px]"
                        style={{ color: isActive ? "#d97706" : "#6b7280" }}
                      >
                        {i + 1}
                      </span>
                    </span>
                  </button>
                  {isActive && quickFilters.length > 0 && (
                    <div className="ml-7 pl-3 border-l-2 border-primary/10 space-y-2 py-1.5 mb-0.5 anim-fade-in">
                      {quickFilters.map((f) => (
                        <FilterControl
                          key={f.id}
                          filter={f}
                          onChange={(v) => handleFilterChange(layer.id, f.id, v)}
                          dynamicOptions={dynamicOptions}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
            <button
              onClick={() => setLayersOpen(true)}
              className="w-full text-sm font-medium text-primary/70 hover:text-primary bg-primary/5 hover:bg-primary/10 rounded-lg py-2 mt-0.5 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              Plus de calques
            </button>
          </div>
        ) : (
          /* Extended mode */
          <div className="flex-1 overflow-y-auto scrollbar-thin">
            {/* Search */}
            <div data-tour-step="layer-search" className="px-3 py-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300 pointer-events-none" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={layerSearch}
                  onChange={(e) => setLayerSearch(e.target.value)}
                  placeholder="Rechercher..."
                  data-layer-search
                  className="transition-[box-shadow,border-color] duration-150 focus:border-[#1e40af] focus:shadow-[0_0_0_3px_rgba(30,64,175,0.08)] w-full text-sm border border-gray-200 rounded-lg pl-8 pr-8 py-1.5 focus:outline-none bg-gray-50/50"
                />
                {layerSearch && (
                  <button
                    onClick={() => setLayerSearch("")}
                    className="cursor-pointer absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-300 hover:text-gray-500 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Layer groups */}
            <div className="px-2 pb-3 space-y-1">
              {filteredLayerGroups.map((group) => (
                <LayerGroupSection
                  key={group.label}
                  group={group}
                  activeLayers={activeLayers}
                  toggleLayer={handleToggleLayer}
                  quickLayers={quickLayers}
                  toggleQuickLayer={toggleQuickLayer}
                  onFilterChange={handleFilterChange}
                  forceSubgroupsOpen={layerSearch.length >= 2}
                  dynamicOptions={dynamicOptions}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
